import React, { useState } from "react";
import {
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Button,
    Typography,
    useMediaQuery,
    DialogTitle,
    Dialog,
    FormHelperText
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import MuiPhoneNumber from "material-ui-phone-number";
import { useForm, Controller } from "react-hook-form";
import { useAtom } from "jotai";
import { userPageSnackbarAtom } from "../atoms/usersTable";
import {
    mbxUserAtom,
    companyAtom,
    openAddClientModalAtom
} from "../atoms/atoms";
import { postApi } from "../modules/AmplifyServices";
import { EMAIL_REGEX } from "../utils/Constants";
import { useTheme } from "@mui/material/styles";
import { ClientAddressInput } from "./ClientAddressInput";
import validator from "validator";
import { cleanPhoneNumber } from "../utils";
import LinkDetectionTextField from "../utils/CommonComonents/LinkDetectionTextField";

const AddClientModal = ({ onClose, handleOnCreate }) => {
    const theme = useTheme();
    const [openAddClientModal, setOpenAddClientModal] = useAtom(
        openAddClientModalAtom
    );
    const [mbxUser] = useAtom(mbxUserAtom);
    const [company] = useAtom(companyAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });

    const form = useForm({
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            sendWelcomeEmail: true,
            address: "",
            addressAttributes: {
                street: "",
                city: "",
                state: "",
                country: "",
                postalcode: "",
                longitude: null,
                latitude: null,
                addressoneline: ""
            },
            notes: ""
        },
        mode: "onSubmit",
        reValidateMode: "onSubmit"
    });
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        clearErrors,
        watch,
        control,
        formState
    } = form;
    const { errors, dirtyFields } = formState;
    register("phone", { required: "Please enter a phone number" });
    const notes = watch("notes");
    const requiredFields = ["firstName", "lastName", "email", "phone"];
    const isDisabled =
        !requiredFields.every((field) => dirtyFields[field]) ||
        cleanPhoneNumber(getValues("phone")).length < 12; //assumes atleast 10 digits, plus country code and '+' sign
    const disableAllFields = isSubmitting;
    const handleClose = () => {
        form.reset();
        setOpenAddClientModal(false);
        if (onClose) {
            onClose();
        }
    };

    const handlePhoneChange = (value) => {
        setValue("phone", value, { shouldDirty: true });
        clearErrors("phone");
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            //append additional data for API use
            data = {
                ...data,
                companyId: company.id,
                currency: company.currency ? company.currency : "CAD",
                role: "CLIENT",
                active: true,
                notesDetails: {
                    notes: notes,
                    createdBy: mbxUser.firstname + " " + mbxUser.lastname,
                    updatedBy: mbxUser.firstname + " " + mbxUser.lastname
                }
            };
            //create route
            const {
                status = null,
                newUserRecord = null,
                message = ""
            } = await postApi("userManagement", "/client", {
                body: data
            });

            if (newUserRecord && status >= 200 && status < 300) {
                onSuccess(
                    newUserRecord,
                    "Client has been successfully created"
                );
            } else {
                onFailure(
                    status,
                    "Unexpected error: The user account was not created"
                );
                throw new Error(
                    `Failed to create new user profile. Error: ${message}`
                );
            }
        } catch (error) {
            console.log("Error submitting form data:", error);
            onFailure(
                null,
                "Unexpected error: The user account was not created"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSuccess = async (userData, successMessage) => {
        if (handleOnCreate) {
            const { client, company: userCompany, ...rest } = userData;
            const clientData = {
                ...client,
                company,
                user: {
                    ...rest
                }
            };
            handleOnCreate(clientData);
        }
        setUserPageSnackbar({
            open: true,
            message: successMessage,
            severity: "success"
        });
        setIsSubmitting(false);
        handleClose();
    };

    const onFailure = (status, errorMessage) => {
        setUserPageSnackbar({
            open: true,
            message: `${errorMessage} ${status ? `(${status})` : ""}`,
            severity: "error"
        });
    };
    return (
        <Dialog open={openAddClientModal} onClose={handleClose}>
            <DialogTitle sx={{ paddingBottom: "32px" }}>Add client</DialogTitle>
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                style={{
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%"
                }}
            >
                <DialogContent
                    sx={{ minHeight: "200px", alignContent: "center" }}
                >
                    <Grid
                        container
                        spacing={2.25}
                        justifyContent={!isDesktop && "center"}
                        alignItems="flex-start"
                    >
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            pt={"0px !important"}
                            pb={"14px"}
                        >
                            <Controller
                                name="sendWelcomeEmail"
                                control={control}
                                render={({
                                    field: { onChange: onChangeFormHook, value }
                                }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                id="sendWelcomeEmail"
                                                name="sendWelcomeEmail"
                                                disabled={disableAllFields}
                                                checked={value}
                                                value={value}
                                                onChange={onChangeFormHook}
                                            />
                                        }
                                        label="Send welcome email"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <TextField
                                id="firstname"
                                name="firstName"
                                type="text"
                                size="small"
                                label="First name"
                                disabled={disableAllFields}
                                autoFocus
                                fullWidth
                                inputProps={{
                                    onBlur: (event) => {
                                        const value = event.target.value;
                                        if (value) {
                                            const formattedName =
                                                value[0].toUpperCase() +
                                                value.slice(1);
                                            setValue(
                                                "firstName",
                                                formattedName
                                            );
                                        }
                                    }
                                }}
                                sx={{
                                    "& fieldset": {
                                        "& legend": {
                                            width: "58px"
                                        }
                                    }
                                }}
                                {...register("firstName", {
                                    required: "Please enter a first name",
                                    onChange: () => clearErrors("firstName")
                                })}
                                error={!!errors.firstName}
                            />
                        </Grid>
                        {errors?.firstName && (
                            <>
                                <Grid
                                    item
                                    variant="errorTextContainer"
                                    xs={12}
                                    sm={6}
                                >
                                    <Typography variant="errorText">
                                        {errors.firstName?.message}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} sm={12}>
                            <TextField
                                id="lastname"
                                name="lastName"
                                type="text"
                                size="small"
                                label="Last name"
                                disabled={disableAllFields}
                                fullWidth
                                inputProps={{
                                    onBlur: (event) => {
                                        const value = event.target.value;
                                        if (value) {
                                            const formattedName =
                                                value[0].toUpperCase() +
                                                value.slice(1);
                                            setValue("lastName", formattedName);
                                        }
                                    }
                                }}
                                sx={{
                                    "& fieldset": {
                                        "& legend": {
                                            width: "58px"
                                        }
                                    }
                                }}
                                {...register("lastName", {
                                    required: "Please enter a last name",
                                    onChange: () => clearErrors("lastName")
                                })}
                                error={!!errors.lastName}
                            />
                        </Grid>

                        {errors?.lastName && (
                            <>
                                <Grid
                                    variant="errorTextContainer"
                                    item
                                    xs={12}
                                    sm={6}
                                >
                                    <Typography variant="errorText">
                                        {errors.lastName?.message}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={12}>
                            <TextField
                                id="email"
                                name="email"
                                type="email"
                                size="small"
                                label="Email"
                                disabled={disableAllFields}
                                fullWidth
                                sx={{
                                    "& fieldset": {
                                        "& legend": {
                                            width: "36px"
                                        }
                                    }
                                }}
                                {...register("email", {
                                    required: "Please enter an email",
                                    pattern: {
                                        value: EMAIL_REGEX,
                                        message: "Invalid email format"
                                    },
                                    validate: {
                                        validateEmail: (fieldValue) =>
                                            validator.isEmail(fieldValue) ||
                                            "Invalid email format"
                                    },
                                    onChange: () => clearErrors("email")
                                })}
                                error={!!errors.email}
                            />
                        </Grid>
                        {errors?.email && (
                            <>
                                <Grid
                                    variant="errorTextContainer"
                                    item
                                    xs={12}
                                    sm={6}
                                >
                                    <Typography variant="errorText">
                                        {errors.email?.message}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} sm={12}>
                            <MuiPhoneNumber
                                id="phone"
                                variant="outlined"
                                label="Phone"
                                disabled={disableAllFields}
                                sx={{
                                    display: "flex",
                                    "& fieldset": {
                                        "& legend": {
                                            width: "39px"
                                        }
                                    }
                                }}
                                size="small"
                                fullWidth
                                defaultCountry={
                                    company?.countrycode3166alpha2
                                        ? company.countrycode3166alpha2.toLowerCase()
                                        : "ca"
                                }
                                value={getValues("phone")}
                                onChange={handlePhoneChange}
                                error={!!errors.phone && !getValues("phone")}
                            />
                        </Grid>

                        {!getValues("phone") && errors?.phone && (
                            <>
                                <Grid
                                    variant="errorTextContainer"
                                    item
                                    xs={12}
                                    sm={6}
                                >
                                    <Typography variant="errorText">
                                        {errors.phone?.message}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={12}>
                            <Controller
                                name="address"
                                control={control}
                                render={({
                                    field: {
                                        onChange: onChangeFormHook,
                                        value: { address, addressAttributes }
                                    }
                                }) => (
                                    <ClientAddressInput
                                        setAddress={(value) => {
                                            onChangeFormHook(
                                                value.addressOneLine
                                            );
                                            setValue("addressAttributes", {
                                                street: value.route,
                                                city: value.city,
                                                state: value.prov,
                                                country: value.country,
                                                postalcode: value.postalCode,
                                                longitude: value.coords.lng,
                                                latitude: value.coords.lat
                                            });
                                        }}
                                        formWidth={"100%"}
                                        formMargin={"0px"}
                                        formMarginLeft={"0px"}
                                        label={"Address"}
                                        addressOneLine={
                                            getValues("address")
                                                ? getValues("address")
                                                : ""
                                        }
                                        autocompleteService={{ current: null }}
                                        smallSize={true}
                                        disableAddress={disableAllFields}
                                    />
                                )}
                            />
                            {!!errors.address && (
                                <FormHelperText error>
                                    {errors?.address.message}
                                </FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} sm={12}>
                            <LinkDetectionTextField
                                label="Client notes (internal)"
                                labelWidth="110px"
                                value={notes}
                                onChange={(newNotes) =>
                                    setValue("notes", newNotes)
                                }
                                marginTop={0}
                                disabled={disableAllFields}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        display: "flex",
                        justifyContent: "end"
                    }}
                >
                    <Grid display={"flex"} gap={"18px"} width={"auto"}>
                        {isDesktop && (
                            <Button
                                onClick={handleClose}
                                variant="cancelButton"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            variant="contained"
                            loading={isSubmitting}
                            disabled={isDisabled}
                            type="submit"
                            sx={{ width: "100%" }}
                        >
                            Save
                        </LoadingButton>
                    </Grid>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddClientModal;
